/**
 * CPV-AI-01 AI-6 · an investigation outline of a signal or an insight, as the server stored it (PRD AI-011, §6.2):
 *
 *   the label and the summary; "What we know" — facts, each with its evidence chips that open the source record;
 *   "Hypotheses to test" — each marked Not verified, with the records it rests on and its "Evidence needed" as a
 *   checklist the reader may tick while working (local to this page, never saved or sent); "Questions for the
 *   assessor"; the limitations. Every heading and every sentence is the server's; nothing is computed here.
 *
 * "Copy to assessment notes" puts the outline's text into the signal assessment's rationale when the page offers that
 * form (`notesTarget`), else on the clipboard. It never signs or submits anything. Feedback (AI-009) as on any answer.
 */
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Dropdown } from 'antd';
import { ClipboardPaste, ExternalLink } from 'lucide-react';
import { toast } from 'react-toastify';
import { AlertBox, ASSISTANT, Chip, InfoHint } from '@/components/cpv-ui';
import { EvidenceChip, Feedback, Section, isInternalLink, useOpenLink } from './AssistantAnswer';
import { EVIDENCE_UNAVAILABLE, investigationNotes } from './assistantApi';
import './assistant.css';

const DRAFT_WORDS = { ASSISTANT_DRAFT: 'Draft, not reviewed' };

function Chips({ keys, byKey, onOpen, unavailable = false }) {
  const refs = (keys || []).map((k) => byKey[k]).filter(Boolean);
  if (!refs.length && !unavailable) return null;
  return (
    <div className="ca-evs">
      {refs.map((ev) => <EvidenceChip key={ev.key} ev={ev} onOpen={onOpen} />)}
      {unavailable ? <Chip tone="warning" title="The source record is outside your access or has changed">Evidence unavailable</Chip> : null}
    </div>
  );
}
Chips.propTypes = { keys: PropTypes.array, byKey: PropTypes.object.isRequired, onOpen: PropTypes.func.isRequired, unavailable: PropTypes.bool };

export default function InvestigationAnswer({ response, onChange, notesTarget = null, testId = 'ca-investigation' }) {
  const open = useOpenLink();
  const a = response.answer || {};
  const heads = a.investigation?.headings || {};
  const byKey = Object.fromEntries((response.evidence || []).map((e) => [e.key, e]));
  const [ticked, setTicked] = useState(() => new Set());
  useEffect(() => { setTicked(new Set()); }, [response.id]);
  const tick = (key) => setTicked((t) => { const n = new Set(t); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const limitations = (a.limitations || []).filter((l) => l !== a.summary);

  const toNotes = async () => {
    const text = investigationNotes(response);
    if (notesTarget) {
      notesTarget(text);
      toast.success('Added to the assessment rationale. Select Assess to review it; nothing is signed or sent.');
      return;
    }
    try { await navigator.clipboard.writeText(text); toast.success('Copied. Paste it into the assessment notes.'); }
    catch { toast.error('The outline could not be copied.'); }
  };
  const sources = (response.evidence || []).filter((e) => isInternalLink(e.deep_link))
    .map((e) => ({ key: e.key, label: `${e.key} · ${e.display_label}`, onClick: () => open(e.deep_link) }));

  return (
    <article className="ca-answer" data-testid={testId} aria-label="Investigation outline">
      <div className="ca-label" data-testid="ca-label">
        <span>{response.label}</span>
        <InfoHint label="Answer details" title="About this outline" testId="ca-answer-info" placement="bottomRight"
          facts={[['Status', DRAFT_WORDS[response.draft_status] || response.draft_status],
            a.investigation?.kind_label ? ['Of', a.investigation.kind_label] : null].filter(Boolean)}>
          Drafted by the CPV assistant from the stored CPV records.
        </InfoHint>
      </div>
      <p className="ca-summary" data-testid="ca-summary">{a.summary}</p>

      {(a.findings || []).length > 0 && (
        <Section title={heads.known} testId="ca-known">
          <ul className="ca-list">
            {a.findings.map((f, i) => (
              <li key={i} className="ca-finding" data-testid={`ca-known-${i}`}>
                <Chip tone="info">Fact</Chip>
                <div className="ca-finding-body">
                  <div>{f.text}</div>
                  <Chips keys={f.evidence} byKey={byKey} onOpen={open} unavailable={f.evidence_status === EVIDENCE_UNAVAILABLE} />
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {(a.hypotheses || []).length > 0 && (
        <Section title={heads.hypotheses} testId="ca-investigation-hypotheses">
          <ol className="ca-list ca-hypotheses">
            {a.hypotheses.map((h, i) => (
              <li key={i} className="ca-hypothesis" data-testid={`ca-hypothesis-${i}`}>
                <div className="ca-finding">
                  <Chip tone="caution">{heads.not_verified}</Chip>
                  <div className="ca-finding-body">
                    <div>{h.text}</div>
                    <Chips keys={h.evidence} byKey={byKey} onOpen={open} />
                  </div>
                </div>
                {(h.evidence_needed || []).length > 0 && (
                  <div className="ca-needed">
                    <div className="ca-needed-title">{heads.evidence_needed}</div>
                    <ul className="ca-needed-list">
                      {h.evidence_needed.map((n, j) => {
                        const key = `${i}:${j}`;
                        return (
                          <li key={key} className="ca-needed-item" data-testid={`ca-needed-${i}-${j}`}>
                            <Checkbox checked={ticked.has(key)} onChange={() => tick(key)} data-testid={`ca-needed-${i}-${j}-tick`}>
                              <span className={ticked.has(key) ? 'ca-needed-done' : undefined}>{n.text}</span>
                            </Checkbox>
                            <Chips keys={n.evidence} byKey={byKey} onOpen={open} />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ol>
          <div className="cu-muted cu-small" data-testid="ca-needed-note">Ticks are for your own checking on this page; they are not saved.</div>
        </Section>
      )}

      {(a.review_questions || []).length > 0 && (
        <Section title={heads.questions} testId="ca-assessor-questions">
          <ul className="ca-bullets">{a.review_questions.map((q, i) => <li key={i}>{q}</li>)}</ul>
        </Section>
      )}

      {limitations.length > 0 && (
        <Section title={heads.limitations} testId="ca-limitations">
          <ul className="ca-bullets cu-muted">{limitations.map((l, i) => <li key={i}>{l}</li>)}</ul>
        </Section>
      )}
      {!(a.findings || []).length && (
        <AlertBox tone="warning" title="Not enough data for an outline" testId="ca-insufficient">{a.summary}</AlertBox>
      )}

      <footer className="ca-answer-foot">
        <div className="ca-actions-row">
          <Button size="small" type="primary" icon={<ClipboardPaste size={ASSISTANT.icon} aria-hidden />} onClick={toNotes}
            data-testid="ca-copy-notes" title={notesTarget ? 'Into the assessment rationale on this page' : 'To the clipboard'}>
            Copy to assessment notes
          </Button>
          <Dropdown disabled={!sources.length} trigger={['click']} menu={{ items: sources }}>
            <Button size="small" icon={<ExternalLink size={ASSISTANT.icon} aria-hidden />} disabled={!sources.length} data-testid="ca-open-source">Open source</Button>
          </Dropdown>
        </div>
        <Feedback response={response} onChange={onChange} />
      </footer>
    </article>
  );
}
InvestigationAnswer.propTypes = { response: PropTypes.object.isRequired, onChange: PropTypes.func, notesTarget: PropTypes.func,
  testId: PropTypes.string };
